import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Polls /api/executions/active every 3s while there is at least one active job,
// then backs off to 30s when idle. Returns { active, count, processing, queued, refresh }.
export function useActiveExecutions() {
  const [active, setActive] = useState([]);
  const timerRef = useRef(null);
  const mountedRef = useRef(true);

  const fetchOnce = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/executions/active/list`);
      if (!mountedRef.current) return [];
      const list = res.data.active || [];
      setActive(list);
      return list;
    } catch {
      return [];
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;

    const scheduleNext = (list) => {
      if (!mountedRef.current) return;
      // Fast poll while any job is active, slow poll otherwise.
      const delay = list.length > 0 ? 3000 : 30000;
      timerRef.current = setTimeout(async () => {
        const next = await fetchOnce();
        scheduleNext(next);
      }, delay);
    };

    (async () => {
      const initial = await fetchOnce();
      scheduleNext(initial);
    })();

    // Detener una generación debe apagar la ruedita y el contador YA, sin
    // esperar el próximo poll (lo dispara quien detiene).
    const onChanged = () => { fetchOnce(); };
    window.addEventListener('executions:changed', onChanged);

    return () => {
      mountedRef.current = false;
      window.removeEventListener('executions:changed', onChanged);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [fetchOnce]);

  const processing = active.filter(a => a.status === 'processing');
  const queued = active.filter(a => a.status === 'queued');

  return {
    active,
    count: active.length,
    processing,
    queued,
    refresh: fetchOnce,
  };
}
